import { analyticsTrackEvent } from '@core/utils/analytics';
import {
  EMAIL_COMPOSE_STARTED,
  EMAIL_CONFIG_COMPLETED,
  EMAIL_CONFIG_DISCONNECTED,
  EMAIL_CONFIG_STARTED,
  EMAIL_REPLY_STARTED,
  EMAIL_SEARCHED,
  EMAIL_SYNC_NOW_CLICKED,
  HOME_OUTLOOK_ITEM_TYPE,
  HOME_VIEW_ALL_CLICKED,
  HOME_WIDGET_ITEM_TYPE,
  NAVIGATION_SOURCE_HOME_PAGE,
  NAVIGATION_SOURCE_OUTLOOK,
} from '@core/constants/analytics';
import type { HomeViewRole } from '@app/constants/home';
import { trackHomeWidgetItemClicked } from '../common/analytics';

/**
 * Event-specific props only.
 * System props (platform, Browser, userId, etc.) are attached by the analytics SDK.
 *
 * Always pass the Home view role explicitly — do not rely on classroom analytics
 * context, which stays stuck on the last opened space role (often educator).
 */

const outlookWidgetProps = (role: HomeViewRole) =>
  ({
    itemType: HOME_WIDGET_ITEM_TYPE,
    navigationSource: NAVIGATION_SOURCE_OUTLOOK,
    role,
  } as const);

const outlookHomePageProps = (role: HomeViewRole) =>
  ({
    itemType: HOME_OUTLOOK_ITEM_TYPE,
    navigationSource: NAVIGATION_SOURCE_HOME_PAGE,
    role,
  } as const);

/** EMAIL_CONFIG_STARTED */
export const trackOutlookConnectClicked = (role: HomeViewRole) => {
  analyticsTrackEvent(EMAIL_CONFIG_STARTED, outlookWidgetProps(role));
};

/** EMAIL_CONFIG_COMPLETED */
export const trackOutlookConnected = (role: HomeViewRole) => {
  analyticsTrackEvent(EMAIL_CONFIG_COMPLETED, outlookWidgetProps(role));
};

/** EMAIL_CONFIG_DISCONNECTED */
export const trackOutlookDisconnected = (role: HomeViewRole) => {
  analyticsTrackEvent(EMAIL_CONFIG_DISCONNECTED, outlookWidgetProps(role));
};

/** EMAIL_COMPOSE_STARTED */
export const trackOutlookComposeClicked = (role: HomeViewRole) => {
  analyticsTrackEvent(EMAIL_COMPOSE_STARTED, outlookHomePageProps(role));
};

/** EMAIL_REPLY_STARTED */
export const trackOutlookReplyClicked = (role: HomeViewRole) => {
  analyticsTrackEvent(EMAIL_REPLY_STARTED, outlookHomePageProps(role));
};

/** EMAIL_SEARCHED */
export const trackOutlookSearched = (role: HomeViewRole) => {
  analyticsTrackEvent(EMAIL_SEARCHED, outlookHomePageProps(role));
};

/** EMAIL_SYNC_NOW_CLICKED */
export const trackOutlookRefreshClicked = (role: HomeViewRole) => {
  analyticsTrackEvent(EMAIL_SYNC_NOW_CLICKED, outlookWidgetProps(role));
};

/** HOME_VIEW_ALL_CLICKED */
export const trackOutlookViewAllClicked = (
  count: number,
  role: HomeViewRole
) => {
  analyticsTrackEvent(HOME_VIEW_ALL_CLICKED, {
    ...outlookWidgetProps(role),
    count,
  });
};

/**
 * HOME_WIDGET_ITEM_CLICKED
 * Sends empty itemId (no email data from Outlook) so Avo validation still passes.
 */
export const trackOutlookEmailOpened = (
  navigationSource: string,
  role: HomeViewRole,
  itemIndex?: number
) => {
  trackHomeWidgetItemClicked({
    navigationSource,
    itemType: HOME_OUTLOOK_ITEM_TYPE,
    itemId: '',
    itemIndex,
    role,
  });
};
